import React from 'react';
import { Helmet } from 'react-helmet';
import { stringToReactComponent } from '../../helpers/general';

// Types
import { SectionComponent } from '@codersclan/gatsby-theme-airfleet/src/@types';
import { Section } from '../../@codersclan/gatsby-theme-airfleet/@types/section';
import { Box, Container } from 'theme-ui';

import { baseStyle } from './style';
import TextFrame from '../../components/textFrame';

interface CustomHTMLProps extends Section {
  content: {
    value: string;
  };
  style: {
    useSiteFormStyle: boolean;
  };
  script: {
    value: string;
  };
  margin: {
    desktop: {
      bottom: string;
      top: string;
    };
    mobile: {
      bottom: string;
      top: string;
    };
    tablet: {
      bottom: string;
      top: string;
    };
  };
  html: {
    value: string;
  };
  background: {
    backgroundSettings: {
      size: string;
      repeat: string;
      mixBlendMode: string;
      attachment: string;
      position: {
        x: string;
        y: string;
      };
    };
    color: string;
  };
  twoColumns?: {
    value: boolean;
  };
  postHtmlContent?: {
    value: string;
  };
  postFormContent?: {
    value: string;
  };
}

const CustomHTML: SectionComponent<CustomHTMLProps> = ({
  background,
  content,
  html,
  margin,
  script,
  style,
  twoColumns,
  postHtmlContent,
  postFormContent,
}) => {
  const bg = background.backgroundSettings;

  const renderWithStyle = () => (
    <Box
      className="form-column"
      sx={{
        backgroundAttachment: bg.attachment,
        backgroundColor: background.color || 'transparent',
        backgroundPosition: `${bg.position.x} ${bg.position.y}`,
        backgroundRepeat: bg.repeat,
        backgroundSize: bg.size,
        m: '50px auto',
        mixBlendMode: bg.mixBlendMode,
        ...baseStyle.main,
      }}
    >
      <Box sx={baseStyle.formBg}>
        <Box>
          {script?.value && (
            <Helmet>{stringToReactComponent(script.value)}</Helmet>
          )}
          {html?.value && (
            <Box dangerouslySetInnerHTML={{ __html: html.value }} />
          )}

          {postHtmlContent?.value && (
            <Box sx={baseStyle.postHtmlContent}>
              <TextFrame content={postHtmlContent.value} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderWithoutStyle = () => (
    <Container>
      {content?.value && (
        <Box
          dangerouslySetInnerHTML={{ __html: content.value }}
          sx={baseStyle.content}
        />
      )}
      {script?.value && <Helmet>{stringToReactComponent(script.value)}</Helmet>}
      {html?.value && <Box dangerouslySetInnerHTML={{ __html: html.value }} />}
      {postHtmlContent?.value && (
        <Box sx={baseStyle.postHtmlContent}>
          <TextFrame content={postHtmlContent.value} />
        </Box>
      )}
    </Container>
  );

  const handleCustomStyle = style.useSiteFormStyle
    ? renderWithStyle()
    : renderWithoutStyle();

  const sectionContent = () => (
    <Box
      as="section"
      className="section-content"
      sx={{
        '@media screen and (max-width: 767px)': {
          m: `${margin.mobile.top || 0} auto ${margin.mobile.bottom || 0}`,
        },
        '@media screen and (min-width: 768px) and (max-width: 1200px)': {
          m: `${margin.tablet.top || 0} auto ${margin.tablet.bottom || 0}`,
        },
        '@media screen and (min-width: 1201px)': {
          m: `${margin.desktop.top || 0} auto ${margin.desktop.bottom || 0}`,
        },
      }}
    >
      {style?.useSiteFormStyle && (
        <Container className="content-column">
          {content?.value && (
            <TextFrame content={content.value} sx={baseStyle.content} />
          )}
        </Container>
      )}
      {handleCustomStyle}
    </Box>
  );

  if (twoColumns?.value)
    return (
      <Container className="two-columns-wrapper" sx={baseStyle.twoColumns}>
        {sectionContent()}
        {postFormContent?.value && (
          <Box
            as="section"
            className="section-post-content"
            sx={baseStyle.postHtmlContent}
          >
            <TextFrame content={postFormContent.value} />
          </Box>
        )}
      </Container>
    );

  return sectionContent();
};

export default CustomHTML;
